import * as RToast from '@radix-ui/react-toast'
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react'
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
import { cn } from '../lib/cn'
import { focusRing } from '../lib/focus'

export type ToastTone = 'info' | 'verified' | 'warning' | 'critical'

export interface ToastOptions {
  title: string
  description?: string
  tone?: ToastTone
  /** ms; `null` keeps it until dismissed — use for anything the user must read. */
  duration?: number | null
  action?: { label: string; onClick: () => void }
}

interface ToastRecord extends ToastOptions {
  id: number
}

const ICONS = {
  info: Info,
  verified: CheckCircle2,
  warning: AlertTriangle,
  critical: XCircle,
} as const

const TONE_STYLES: Record<ToastTone, string> = {
  info: 'border-state-info-border',
  verified: 'border-state-verified-border',
  warning: 'border-state-warning-border',
  critical: 'border-state-critical-border',
}

const ICON_TONE: Record<ToastTone, string> = {
  info: 'text-state-info',
  verified: 'text-state-verified',
  warning: 'text-state-warning',
  critical: 'text-state-critical',
}

const ToastContext = createContext<((options: ToastOptions) => void) | null>(null)

/** Imperative toast API. Throws if used outside the provider, rather than silently no-op'ing. */
export function useToast() {
  const push = useContext(ToastContext)
  if (!push) throw new Error('useToast must be used inside <ToastProvider>')
  return push
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastRecord[]>([])

  const push = useCallback((options: ToastOptions) => {
    setToasts((current) => [...current, { ...options, id: Date.now() + Math.random() }])
  }, [])

  const value = useMemo(() => push, [push])

  return (
    <ToastContext.Provider value={value}>
      <RToast.Provider swipeDirection="right" duration={6000}>
        {children}
        {toasts.map((toast) => {
          const tone = toast.tone ?? 'info'
          const Icon = ICONS[tone]
          return (
            <RToast.Root
              key={toast.id}
              duration={toast.duration === null ? Infinity : toast.duration}
              onOpenChange={(open) => {
                if (!open) setToasts((current) => current.filter((t) => t.id !== toast.id))
              }}
              className={cn(
                'flex items-start gap-3 rounded-md border bg-surface-overlay p-4 shadow-lg',
                'data-[state=open]:animate-slide-up',
                TONE_STYLES[tone],
              )}
            >
              <Icon aria-hidden className={cn('mt-0.5 size-4 shrink-0', ICON_TONE[tone])} />
              <div className="flex flex-col gap-1">
                <RToast.Title className="text-body-sm font-medium text-text-primary">
                  {toast.title}
                </RToast.Title>
                {toast.description && (
                  <RToast.Description className="text-caption text-text-secondary">
                    {toast.description}
                  </RToast.Description>
                )}
              </div>
              {toast.action && (
                <RToast.Action
                  altText={toast.action.label}
                  onClick={toast.action.onClick}
                  className={cn(
                    'ms-auto rounded-md px-2 py-1 text-caption font-medium text-text-link hover:bg-surface-hover',
                    focusRing,
                  )}
                >
                  {toast.action.label}
                </RToast.Action>
              )}
              <RToast.Close
                aria-label="Dismiss notification"
                className={cn(
                  'ms-auto rounded-md p-1 text-text-secondary hover:bg-surface-hover hover:text-text-primary',
                  focusRing,
                )}
              >
                <X aria-hidden className="size-3.5" />
              </RToast.Close>
            </RToast.Root>
          )
        })}
        <RToast.Viewport className="fixed bottom-0 end-0 z-toast flex w-full max-w-sm flex-col gap-2 p-4 outline-none" />
      </RToast.Provider>
    </ToastContext.Provider>
  )
}
